import { pickBy } from "lodash";
import { File, FileText, FileVideo2, Mic } from "lucide-react";
import { toast } from "sonner";
import * as _ from "lodash";

// Role codes
export const ROLES = {
  SUPER_ADMIN: "YWRtaW4=", // super admin (base64 encoded)
  SCHOOL_ADMIN: "c2Nob29s", // school admin (base64 encoded)
  TEACHER: "dGVhY2hlcg==", // teacher (base64 encoded)
  STUDENT: "c3R1ZGVudA==", // student (base64 encoded)
  GROUP_SCHOOL_ADMIN: "Z3JvdXBfYWRtaW4=", // group school admin (base64 encoded)
};

export const ROLE_NAMES = {
  "YWRtaW4=": "Super Admin",
  "c2Nob29s": "School Admin",
  "dGVhY2hlcg==": "Teacher",
  "c3R1ZGVudA==": "Student",
  "Z3JvdXBfYWRtaW4=": "Group School Admin",
};

// Role Permissions
export const ROLE_PERMISSIONS = {
  // Header Left Menus
  TAB_DASHBOARD: "tab_dashboard",
  TAB_SCHOOL_MANAGEMENT: "tab_school_management",
  TAB_COURSE_MANAGEMENT: "tab_course_management",
  TAB_ACTIVITY_LOG: "tab_activity_log",
  TAB_ACTIVITY_STUDENT: "tab_activity_student",
  TAB_ACTIVITY_SCHOOL: "tab_activity_school",
  TAB_ACTIVITY_TEACHER: "tab_activity_teacher",
  TAB_USER_MANAGEMENT: "tab_user_management",
  TAB_SUBMISSION_MANAGEMENT: "tab_submission_management",
  TAB_ASSIGNMENT_MANAGEMENT: "tab_assignment",
  TAB_STUDENT_LOG: "tab_student_log",
  TAB_FEEDBACK: "tab_feedbacks",
  TAB_MY_COURSES: "tab_my_courses",
  TAB_MY_CLASS: "tab_my_class",
  TAB_GROUP_SCHOOL_ADMIN: "tab_group_school_admin",
  // Header Right Menus
  BUTTON_NOTIFICATION: "button_notification",
  ACTION_LOGOUT: "action_logout",

  // School Management
  BUTTON_ADD_SCHOOL: "button_add_school",
  BUTTON_EXPORT_SCHOOL: "button_export_school",
  ACTION_DELETE_SCHOOL: "action_delete_school",
  ACTION_EDIT_SCHOOL: "action_edit_school",
  ACTION_VIEW_SCHOOL: "action_view_school",
  ACTION_ACTIVE_INACTIVE_SCHOOL: "action_active_inactive_school",
  ACTION_RESTRICTION_SCHOOL: "action_restriction_school",

  // Student Management
  BUTTON_ADD_STUDENT: "button_add_student",
  ACTION_DELETE_STUDENT: "action_delete_student",
  ACTION_EDIT_STUDENT: "action_edit_student",
  ACTION_VIEW_STUDENT: "action_view_student",
  BUTTON_EXPORT_STUDENT: "button_export_student",
  // Teacher Management
  BUTTON_ADD_TEACHER: "button_add_teacher",
  // BUTTON_EXPORT_TEACHER: "button_export_teacher",
  ACTION_DELETE_TEACHER: "action_delete_teacher",
  ACTION_EDIT_TEACHER: "action_edit_teacher",
  ACTION_VIEW_TEACHER: "action_view_teacher",
  ACTION_ASSIGN_STUDENTS: "action_assign_student",

  // Assignments
  BUTTON_ADD_ASSIGNMENT: "button_create_assignment",
  BUTTON_EXPORT_ASSIGNMENT: "button_export_assignment",
  ACTION_DELETE_ASSIGNMENT: "action_delete_assignment",
  ACTION_EDIT_ASSIGNMENT: "action_edit_assignment",
  ACTION_VIEW_ASSIGNMENT: "action_view_assignment",

  // Submissions
  BUTTON_EXPORT_SUBMISSION: "button_export_submission",
  ACTION_VIEW_SUBMISSION: "action_view_submission",

  // Feedback Management
  BUTTON_EXPORT_FEEDBACK: "button_export_feedback",
  ACTION_VIEW_FEEDBACK: "action_view_feedback",
  ACTION_ADD_SUBMISSION: "action_add_submission",
  ACTION_RECORD_SUBMISSION: "action_record_submission",
  ACTION_UPLOAD_SUBMISSION: "action_upload_submission",

  // Course Management
  BUTTON_EXPORT_COURSE: "button_export_course",
  BUTTON_CREATE_COURSE: "button_create_course",
  ACTION_VIEW_COURSE: "action_view_course",
  ACTION_EDIT_COURSE: "action_edit_course",
  ACTION_DELETE_COURSE: "action_delete_course",
  ACTION_ACTIVE_INACTIVE_COURSE: "action_active_inactive_course",
  ACTION_TERM_RESTRICTION: "action_term_restriction",

  // Class Management (catalog products — mock until API)
  TAB_CLASS_MANAGEMENT: "tab_class_management",
  BUTTON_CREATE_CLASS: "button_create_class",
  BUTTON_EXPORT_CLASS: "button_export_class",
  ACTION_VIEW_CLASS: "action_view_class",
  ACTION_EDIT_CLASS: "action_edit_class",
  ACTION_TOGGLE_CLASS_ACTIVE: "action_toggle_class_active",

  // Activity Log
  BUTTON_EXPORT_STUDENT_LOG: "button_export_student_log",
  BUTTON_EXPORT_SCHOOL_LOG: "button_export_school_log",

  // Group School Admin Management
  BUTTON_ADD_GROUP_SCHOOL_ADMIN: "button_add_group_school_admin",
  BUTTON_EXPORT_GROUP_SCHOOL_ADMIN: "button_export_group_school_admin",
  ACTION_DELETE_GROUP_SCHOOL_ADMIN: "action_delete_group_school_admin",
  ACTION_EDIT_GROUP_SCHOOL_ADMIN: "action_edit_group_school_admin",
  ACTION_VIEW_GROUP_SCHOOL_ADMIN: "action_view_group_school_admin",
  ACTION_ACTIVE_INACTIVE_GROUP_SCHOOL_ADMIN: "action_active_inactive_group_school_admin",
};

export const FileFormats: {
  [key: string]: { type: string; extension: string; name: string };
} = {
  pdf: {
    type: "application/pdf",
    extension: "pdf",
    name: "PDF",
  },
  xl: {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    extension: "xlsx",
    name: "Excel",
  },
  csv: {
    type: "text/csv",
    extension: "csv",
    name: "CSV",
  },
};

export const RESTRICTION = [
  { label: "Hours Per Week", value: "hours" },
  { label: "Days Per Week", value: "days" },
  { label: "Time Range", value: "time" },
];

export const downloadFileFromBlob = (
  response: any,
  type: "pdf" | "xl" | "csv",
  fileName: string
) => {
  // Create a blob from the response data
  const blob = new Blob([response.data], {
    type: FileFormats[type].type,
  });

  // Create a URL for the blob
  const url = window.URL.createObjectURL(blob);

  // Create a temporary anchor element
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", `${fileName}.${FileFormats[type].extension}`);

  // Append the anchor to the body
  document.body.appendChild(link);

  // Trigger the download
  link.click();

  // Clean up
  link.parentNode?.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// Helper function to check if a user has permission to access a menu item
export const hasMenuPermission = (
  userRole: string | undefined,
  menuPermission: string[]
): boolean => {
  if (!userRole) return false;
  return menuPermission?.includes(userRole);
};

export const hasRolePermissions = (
  userPermissions: string[] | undefined,
  menuPermission: string[]
): boolean => {
  if (!userPermissions) return false;
  return menuPermission.some((permission) =>
    userPermissions.includes(permission)
  );
};

/** Super Admin only — Class Management + Level Management routes and nav items */
export const CATALOG_ADMIN_ROLES = [ROLES.SUPER_ADMIN];

export const canAccessNavItem = (
  userPermissions: string[] | undefined,
  userRoleCode: string | undefined,
  item: { permissions: string[]; roles?: string[] }
): boolean => {
  if (!hasRolePermissions(userPermissions, item.permissions)) return false;
  if (item.roles?.length && !item.roles.includes(userRoleCode ?? "")) {
    return false;
  }
  return true;
};

export const getInitials = (...names: any[]) => {
  return names
    .flatMap((name) => (name ? name?.split(" ") : ["", ""])) // Split each name into words
    .map((word) => word.charAt(0).toUpperCase()) // Get the first letter of each word
    .filter(Boolean) // Remove any empty values
    .slice(0, 2) // Limit to the first two initials
    .join(""); // Join them together
};

export const capitalizeFirstLetter = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const removeEmptyProps = (obj: any) => {
  // return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v != null));
  return pickBy(
    obj,
    (value) => value !== null && value !== undefined && value !== ""
  );
};

// Success toast with green check icon
export const successToast = (title: string, message: string) => {
  toast.custom((t) => (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2">
        <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center">
          <svg
            width={18}
            height={18}
            viewBox="0 0 18 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M7.83317 12.8337L13.7082 6.95866L12.5415 5.79199L7.83317 10.5003L5.45817 8.12533L4.2915 9.29199L7.83317 12.8337ZM8.99984 17.3337C7.84706 17.3337 6.76373 17.1149 5.74984 16.6774C4.73595 16.2399 3.854 15.6462 3.104 14.8962C2.354 14.1462 1.76025 13.2642 1.32275 12.2503C0.885254 11.2364 0.666504 10.1531 0.666504 9.00033C0.666504 7.84755 0.885254 6.76421 1.32275 5.75033C1.76025 4.73644 2.354 3.85449 3.104 3.10449C3.854 2.35449 4.73595 1.76074 5.74984 1.32324C6.76373 0.885742 7.84706 0.666992 8.99984 0.666992C10.1526 0.666992 11.2359 0.885742 12.2498 1.32324C13.2637 1.76074 14.1457 2.35449 14.8957 3.10449C15.6457 3.85449 16.2394 4.73644 16.6769 5.75033C17.1144 6.76421 17.3332 7.84755 17.3332 9.00033C17.3332 10.1531 17.1144 11.2364 16.6769 12.2503C16.2394 13.2642 15.6457 14.1462 14.8957 14.8962C14.1457 15.6462 13.2637 16.2399 12.2498 16.6774C11.2359 17.1149 10.1526 17.3337 8.99984 17.3337Z"
              fill="#6FFF98"
            />
          </svg>
        </div>
        <div>
          <div className="font-semibold text-white">{title}</div>
          <div className="text-white opacity-90 text-sm">{message}</div>
        </div>
      </div>
      <div
        className="rounded-full flex items-center justify-end cursor-pointer"
        onClick={() => toast.dismiss(t)}
      >
        <svg
          width={18}
          height={18}
          viewBox="0 0 18 18"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <mask
            id="mask0_739_20546"
            style={{
              maskType: "alpha",
            }}
            maskUnits="userSpaceOnUse"
            x={0}
            y={0}
            width={18}
            height={18}
          >
            <rect width={18} height={18} fill="#D9D9D9" />
          </mask>
          <g mask="url(#mask0_739_20546)">
            <path
              d="M4.8 14.25L3.75 13.2L7.95 9L3.75 4.8L4.8 3.75L9 7.95L13.2 3.75L14.25 4.8L10.05 9L14.25 13.2L13.2 14.25L9 10.05L4.8 14.25Z"
              fill="#FCFCFC"
            />
          </g>
        </svg>
      </div>
    </div>
  ));
};

// Error toast with red cross icon
export const errorToast = (title: string, message: string) => {
  toast.custom((t) => (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center">
        <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center">
          <svg
            width={20}
            height={20}
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <mask
              id="mask0_739_20364"
              style={{
                maskType: "alpha",
              }}
              maskUnits="userSpaceOnUse"
              x={0}
              y={0}
              width={20}
              height={20}
            >
              <rect width={20} height={20} fill="#D9D9D9" />
            </mask>
            <g mask="url(#mask0_739_20364)">
              <path
                d="M6.875 17.5L2.5 13.125V6.875L6.875 2.5H13.125L17.5 6.875V13.125L13.125 17.5H6.875ZM7.625 13.5417L10 11.1667L12.375 13.5417L13.5417 12.375L11.1667 10L13.5417 7.625L12.375 6.45833L10 8.83333L7.625 6.45833L6.45833 7.625L8.83333 10L6.45833 12.375L7.625 13.5417Z"
                fill="#FF6F6F"
              />
            </g>
          </svg>
        </div>
        <div>
          <div className="font-semibold text-white">{title}</div>
          <div className="text-white opacity-90 text-sm">{message}</div>
        </div>
      </div>
      <div
        className="rounded-full flex items-center justify-end cursor-pointer"
        onClick={() => toast.dismiss(t)}
      >
        <svg
          width={18}
          height={18}
          viewBox="0 0 18 18"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <mask
            id="mask0_739_20546"
            style={{
              maskType: "alpha",
            }}
            maskUnits="userSpaceOnUse"
            x={0}
            y={0}
            width={18}
            height={18}
          >
            <rect width={18} height={18} fill="#D9D9D9" />
          </mask>
          <g mask="url(#mask0_739_20546)">
            <path
              d="M4.8 14.25L3.75 13.2L7.95 9L3.75 4.8L4.8 3.75L9 7.95L13.2 3.75L14.25 4.8L10.05 9L14.25 13.2L13.2 14.25L9 10.05L4.8 14.25Z"
              fill="#FCFCFC"
            />
          </g>
        </svg>
      </div>
    </div>
  ));
};

export const getFileConfig = (file: {
  file_type: string;
  url: string;
  id: string;
}) => {
  if (!file.file_type) {
    return {
      fileType: "View File",
      icon: <FileText className="w-5 h-5 text-[#2165C5]" />,
    };
  }

  if (file.file_type.toLowerCase().includes("video")) {
    return {
      fileType: "Play Video",
      icon: <FileVideo2 className="w-5 h-5 text-[#2165C5]" />,
    };
  }
  if (file.file_type.toLowerCase().includes("audio")) {
    return {
      fileType: "Play Audio",
      icon: <Mic className="w-5 h-5 text-[#2165C5]" />,
    };
  }
  if (file.file_type.toLowerCase().includes("pdf")) {
    return {
      fileType: "View PDF",
      icon: <File className="w-5 h-5 text-[#2165C5]" />,
    };
  }
  if (file.file_type.toLowerCase().includes("document")) {
    return {
      fileType: "View Document",
      icon: <FileText className="w-5 h-5 text-[#2165C5]" />,
    };
  }

  // Default return if none of the conditions match
  return {
    fileType: "View File",
    icon: <FileText className="w-5 h-5 text-[#2165C5]" />,
  };
};

export const PDFIcon = () => (
  <img src="/images/pdf_icon.png" alt="PDF" className="w-5 h-6" />
);

export const ExcelIcon = () => (
  <img src="/images/excel_icon.png" alt="Excel" className="w-5 h-5" />
);

export const CSVIcon = () => (
  <img src="/images/csv_icon.png" alt="CSV" className="w-5 h-5" />
);

export const evalCommonStyles: any = {
  feedback: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '8px 10px',
    gap: '10px',
    borderRadius: '70px',
    borderWidth: '1px',
    background: '#F6F7FA',
    border: '1px solid #DADDE2',
  },
  excellent_performance: {
    background: '#DDFDF0',
    border: '1px solid #A3F5D4'
  },
  very_good_performance: {
    background: '#D5EEFF',
    border: '1px solid #A5DAFF'
  },
  fair_performance: {
    background: '#FEF3D7',
    border: '1px solid #FBE2A3'
  },
  needs_improvement: {
    background: '#FCE1D6',
    border: '1px solid #FFC2A9'
  }
}
export const evaluationColumns = (grade: any) => {
  const evalColumns: any[] = [
    { key: "overall_percentage1", referenceKey: 'overall_percentage', index: 0, title: "Evaluation 1", props: { minWidth: 150, maxWidth: 150 } },
    { key: "remarks1", referenceKey: 'remarks', index: 0, title: "Remarks", props: { minWidth: 250, maxWidth: 250 } },
    { key: "feedback1", referenceKey: 'feedback', index: 0, title: "Feedback", props: { minWidth: 250, maxWidth: 250 } },
    { key: "overall_percentage2", referenceKey: 'overall_percentage', index: 1, title: "Evaluation 2", props: { minWidth: 150, maxWidth: 150 } },
    { key: "remarks2", referenceKey: 'remarks', index: 1, title: "Remarks", props: { minWidth: 250, maxWidth: 250 } },
    { key: "feedback2", referenceKey: 'feedback', index: 1, title: "Feedback", props: { minWidth: 250, maxWidth: 250 } },
  ]
  const columns: any[] = [];
  const getDisplayValue = (referenceKey: string, value: any) => {
    switch (referenceKey) {
      case 'overall_percentage':
        return `${value} %`;
      case 'remarks':
        return <div style={evalCommonStyles.remarks}>
          {value}
        </div>;
      case 'feedback':
        return <div style={{ ...evalCommonStyles.feedback, ...evalCommonStyles[value?.toLowerCase().replace(/\s+/g, '_')] }}>
          {value}
        </div>;
    }
    return value !== undefined && value !== null && value !== '' ? value : '-';
  }
  evalColumns.forEach((col) => {
    columns.push({
      key: col.key,
      title: col.title,
      dataIndex: col.key,
      ...col.props,
      render: (__: any, record: any) => {
        const evaluations: any[] = _.find(record.grade, ['id', grade])?.evaluations || [];
        return <div className="text-[#1F1F1F] text-[16px] font-medium">
          {
            evaluations && evaluations[col.index] ?
              getDisplayValue(col.referenceKey, evaluations[col.index][col.referenceKey]) :
              '-'
          }
        </div>;
      },
    });
  });
  return columns;
}